import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUIStore } from './uiStore'
import { AuthService } from '@/services/AuthService'
import type { User } from '@/models/User'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const uiStore = useUIStore()
  const authService = new AuthService()

  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isAuthenticated = computed(() => !!token.value)

  // Actions
  const initialize = async () => {
    try {
      const storedToken = localStorage.getItem('authToken')
      if (!storedToken) return

      // If the token is already expired client-side, drop it BEFORE making
      // any API call. Otherwise fetchUser() would trigger a logout() that
      // pushes to /login while Vue Router is still resolving the initial
      // navigation, causing a flash of the wrong login page on first load.
      if (isJwtExpired(storedToken)) {
        localStorage.removeItem('authToken')
        return
      }

      token.value = storedToken
      await fetchUser()
    } catch (error) {
      console.error('Auth initialization failed:', error)
      logout()
    }
  }

  function isJwtExpired(jwt: string): boolean {
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]))
      return typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()
    } catch {
      // Malformed token — treat as expired so it gets cleared.
      return true
    }
  }

  const login = async (username: string, password: string) => {
    try {
      uiStore.setLoading(true)
      const response = await authService.login(username, password)

      token.value = response.token
      localStorage.setItem('authToken', response.token)

      await fetchUser()
      return true
    } catch (error) {
      uiStore.showError('Login failed. Please check your credentials.')
      throw error
    } finally {
      uiStore.setLoading(false)
    }
  }

  const fetchUser = async () => {
    try {
      if (!token.value) return

      const response = await authService.getCurrentUser()
      user.value = response
    } catch (error) {
      console.error('Failed to fetch user:', error)
      logout()
    }
  }

  const logout = async () => {
    try {
      if (token.value) {
        await authService.logout()
      }
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      // Clear state
      user.value = null
      token.value = null
      localStorage.removeItem('authToken')

      // Redirect to login
      if (router.currentRoute.value.meta.requiresAuth) {
        await router.push('/login')
      }
    }
  }

  const checkAuth = (): boolean => {
    if (!isAuthenticated.value) {
      router.push('/login')
      return false
    }
    return true
  }

  return {
    user,
    token,
    isAuthenticated,
    initialize,
    login,
    logout,
    checkAuth
  }
})